// vg-binding: CONTEXT:decisions  (P999.11.D-09/D-14/D-15/D-16 tokens + D-04 dark-ok)
// vg-binding: INTERFACE-STANDARDS:error-shape  (pure FE restyle — no API surface changed)
// apps/admin/src/pages/jobs/DeadLetterQueuePage.tsx
//
// Phase 3.5 Wave 8 Task 23 — Admin Dead Letter Queue (DLQ) retry / discard UI.
//
// Per CONTEXT.md (invoice-generation DLQ) + (BullMQ failure
// retention + audit trail). Covers goals: G-FE-ADMIN-DLQ-01, G-JOB-03.
// Reference design: <design-ref>no-asset:phase-3.5-pencil-no-dlq-screen</design-ref>
// (Form B — no PNG mockup, layout follows DESIGN.md §form-and-table conventions
// shared by Phase 3.2 admin sibling pages).
//
// Endpoints consumed (API-CONTRACTS §8 Admin Job Queue, lines 2429-2553):
//   GET    /api/v1/admin/jobs/dlq?queue_name=&date_from=&date_to=&page=&page_size=&sort=
//   POST   /api/v1/admin/jobs/dlq/:id/retry      (Idempotency-Key required)
//   POST   /api/v1/admin/jobs/dlq/:id/discard    (Idempotency-Key required, body: {reason})
//
// Backend handlers land in Task 24 (BullMQ queue registration). When BE is not
// yet deployed the page surfaces an empty state + toast on 404 (graceful
// degradation per VG executor §error-handling-rule-4).
//
// UI structure:
//   - Header (title + total count pill + refresh)
//   - Filter bar: queue dropdown (all/invoice-generation/tax-bundle/notification-email/
//     receipt-generation), date_from, date_to
//   - Bulk action bar (visible when >=1 row selected): Retry selected / Discard selected
//   - Table: select | queue | job_id | failed_at | attempts | error message | actions
//   - Per-row actions: Retry (confirm) / Discard (reason form) / View payload (modal)
//   - Pagination (« ‹ ±5 › » + "Showing X–Y of Z")
//   - URL state sync (queue_name, date_from, date_to, page) per VG R7
//
// Note on receipt-generation: Task body lists 4 queues but API-CONTRACTS Block 2
// schema enums only 3 (`invoice-generation | tax-bundle | notification-email`).
// Receipt-generation is added per Task 22b (AC-108) and pending-reminder per
// Task 15b — both will land in Task 24 contract enum extension. We accept any
// queue_name string from the server (not enum-narrowed) so future additions
// don't break the page.
//
// vg-binding: P9.2.D-01 — no hardcoded VN literal in user-visible TSX
// vg-binding: P9.2.D-02 — minimum diff (literal-replace + t() wrap only)
// vg-binding: P9.2.D-04 — locale parity (en/vi/fr)
// vg-binding: P9.2.D-11 — i18n key namespace admin.syncErrors.dlq.*
// vg-binding: P9.2.D-12 — PR scope ≤10 files

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useSearchParams } from 'react-router-dom';

import { apiClient } from '@printway/shared/api';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogHeader,
  DialogTitle,
  Input,
  PageHeader,
  Pagination,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  cn,
  useToast,
} from '@printway/ui';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';

import {
  envelopeError,
  newIdempotencyKey,
  unwrap,
  type LooseClient,
} from '../billing/billing-shared.js';
import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Wire types — mirror API-CONTRACTS §8 (lines 2451-2466 + 2487-2491 + 2527-2535).
// queue_name kept as `string` rather than enum-narrowed so Task 22b/15b queue
// additions (receipt-generation, pending-reminder) don't break the FE.
// ---------------------------------------------------------------------------

interface DlqJobRow {
  job_id: string;
  queue_name: string;
  failed_at: string;
  attempts: number;
  last_error_message: string;
  payload: Record<string, unknown>;
}

interface DlqListResponse {
  rows: DlqJobRow[];
  total: number;
  page: number;
  page_size: number;
}

// Queue catalog — full set per task body (4 queues) + Task 22b receipt
// + Task 15b pending-reminder. Filter dropdown shows all known queues; the
// server is the source of truth and may return others.
// Labels resolved via t(`syncErrors.dlq.queueOptions.${labelKey}`).
const QUEUE_OPTIONS: Array<{ value: string; labelKey: string }> = [
  { value: '', labelKey: 'all' },
  { value: 'invoice-generation', labelKey: 'invoiceGeneration' },
  { value: 'tax-bundle', labelKey: 'taxBundle' },
  { value: 'notification-email', labelKey: 'notificationEmail' },
  { value: 'receipt-generation', labelKey: 'receiptGeneration' },
  { value: 'pending-reminder', labelKey: 'pendingReminder' },
];

const PAGE_SIZE = 20;

// Radix Select forbids SelectItem value="" — sentinel for the "all" option.
const ALL_VALUE = '__all__';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type Translator = (key: string, vars?: Record<string, unknown>) => string;

function relativeTime(iso: string, t: Translator): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return iso;
  const sec = Math.floor((Date.now() - then) / 1000);
  if (sec < 60) return t('syncErrors.common.secondsAgo', { value: sec });
  const min = Math.floor(sec / 60);
  if (min < 60) return t('syncErrors.common.minutesAgo', { value: min });
  const hr = Math.floor(min / 60);
  if (hr < 24) return t('syncErrors.common.hoursAgo', { value: hr });
  const days = Math.floor(hr / 24);
  if (days < 30) return t('syncErrors.common.daysAgo', { value: days });
  return new Date(iso).toLocaleDateString();
}

function queueBadgeClass(queue: string): string {
  switch (queue) {
    case 'invoice-generation':
      return 'bg-indigo-100 dark:bg-indigo-950/40 text-indigo-800 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800';
    case 'tax-bundle':
      return 'bg-purple-100 dark:bg-purple-950/40 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-800';
    case 'notification-email':
      return 'bg-sky-100 dark:bg-sky-950/40 text-sky-800 dark:text-sky-300 border-sky-200 dark:border-sky-800';
    case 'receipt-generation':
      return 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800';
    case 'pending-reminder':
      return 'bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800';
    default:
      return 'bg-pw-surface text-pw-text border-pw-border';
  }
}

function truncate(s: string, n: number): string {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DeadLetterQueuePage(): JSX.Element {
  const { toast } = useToast();
  const { t } = useTranslation('admin');
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState<DlqJobRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  // Selection (for bulk actions).
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Per-row action state.
  const [retryTarget, setRetryTarget] = useState<DlqJobRow | null>(null);
  const [discardTarget, setDiscardTarget] = useState<DlqJobRow | null>(null);
  const [discardReason, setDiscardReason] = useState('');
  const [discardError, setDiscardError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Bulk action state.
  const [bulkRetry, setBulkRetry] = useState(false);
  const [bulkDiscard, setBulkDiscard] = useState(false);
  const [bulkDiscardReason, setBulkDiscardReason] = useState('');
  const [bulkDiscardError, setBulkDiscardError] = useState<string | null>(null);

  // Payload viewer.
  const [payloadView, setPayloadView] = useState<DlqJobRow | null>(null);

  // ---- URL state ----
  const filterQueue = searchParams.get('queue_name') ?? '';
  const filterDateFrom = searchParams.get('date_from') ?? '';
  const filterDateTo = searchParams.get('date_to') ?? '';
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);

  function updateParam(key: string, value: string): void {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    // Any filter change resets pagination to page 1.
    if (key !== 'page') next.delete('page');
    setSearchParams(next, { replace: true });
  }

  // ---- List query ----
  const listQueryString = useMemo(() => {
    const params = new URLSearchParams();
    if (filterQueue) params.set('queue_name', filterQueue);
    if (filterDateFrom) params.set('date_from', filterDateFrom);
    if (filterDateTo) params.set('date_to', filterDateTo);
    params.set('page', String(page));
    params.set('page_size', String(PAGE_SIZE));
    params.set('sort', '-failed_at');
    return params.toString();
  }, [filterQueue, filterDateFrom, filterDateTo, page]);

  const fetchRows = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const client = apiClient() as unknown as LooseClient;
      const { data, error, response } = await client.GET(
        `/api/v1/admin/jobs/dlq?${listQueryString}`,
      );
      if (error || !response.ok) {
        // 404 likely = backend route not registered yet (Task 24 pending) —
        // surface a non-blocking informational toast and render empty state.
        if (response.status === 404) {
          toast({
            title: t('syncErrors.dlq.toast.endpointNotReadyTitle'),
            description: t('syncErrors.dlq.toast.endpointNotReadyDesc'),
            variant: 'warning',
          });
        } else {
          toast({
            title: t('syncErrors.dlq.toast.loadFailedTitle'),
            description: envelopeError(error) ?? `HTTP ${response.status}`,
            variant: 'error',
          });
        }
        setRows([]);
        setTotal(0);
        return;
      }
      const payload = unwrap<DlqListResponse>(data);
      setRows(payload.rows ?? []);
      setTotal(payload.total ?? 0);
      // Drop any selections for jobs no longer in the page.
      setSelected((prev) => {
        const next = new Set<string>();
        const ids = new Set((payload.rows ?? []).map((r) => r.job_id));
        prev.forEach((id) => {
          if (ids.has(id)) next.add(id);
        });
        return next;
      });
    } catch (err) {
      toast({
        title: t('syncErrors.dlq.toast.networkErrorTitle'),
        description: err instanceof Error ? err.message : 'Unknown',
        variant: 'error',
      });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [listQueryString, toast, t]);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows, refreshTick]);

  // ---- Selection helpers ----

  function toggleAll(): void {
    setSelected((prev) => {
      if (prev.size === rows.length && rows.length > 0) return new Set();
      return new Set(rows.map((r) => r.job_id));
    });
  }

  function toggleRow(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // ---- Single retry ----

  async function handleRetry(job: DlqJobRow): Promise<void> {
    setSubmitting(true);
    try {
      const client = apiClient() as unknown as LooseClient;
      const { error, response } = await client.POST(
        `/api/v1/admin/jobs/dlq/${encodeURIComponent(job.job_id)}/retry`,
        {
          headers: { 'Idempotency-Key': newIdempotencyKey() },
          body: {},
        },
      );
      if (error || !response.ok) {
        if (response.status === 409) {
          toast({
            title: t('syncErrors.dlq.toast.alreadyRetriedTitle'),
            description: t('syncErrors.dlq.toast.alreadyRetriedDesc'),
            variant: 'warning',
          });
        } else {
          toast({
            title: t('syncErrors.dlq.toast.retryFailedTitle'),
            description: envelopeError(error) ?? `HTTP ${response.status}`,
            variant: 'error',
          });
        }
        return;
      }
      toast({
        title: t('syncErrors.dlq.toast.retryQueuedTitle'),
        description: t('syncErrors.dlq.toast.retryQueuedDesc', {
          id: truncate(job.job_id, 12),
          queue: job.queue_name,
        }),
        variant: 'success',
      });
    } catch (err) {
      toast({
        title: t('syncErrors.dlq.toast.networkErrorTitle'),
        description: err instanceof Error ? err.message : 'Unknown',
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
      setRetryTarget(null);
      setRefreshTick((n) => n + 1);
    }
  }

  // ---- Single discard ----

  async function handleDiscard(): Promise<void> {
    if (!discardTarget) return;
    const reason = discardReason.trim();
    if (reason.length < 1) {
      setDiscardError(t('syncErrors.dlq.discardDialog.errorTooShort'));
      return;
    }
    if (reason.length > 500) {
      setDiscardError(t('syncErrors.dlq.discardDialog.errorTooLong'));
      return;
    }
    setSubmitting(true);
    try {
      const client = apiClient() as unknown as LooseClient;
      const { error, response } = await client.POST(
        `/api/v1/admin/jobs/dlq/${encodeURIComponent(discardTarget.job_id)}/discard`,
        {
          headers: { 'Idempotency-Key': newIdempotencyKey() },
          body: { reason },
        },
      );
      if (error || !response.ok) {
        toast({
          title: t('syncErrors.dlq.toast.discardFailedTitle'),
          description: envelopeError(error) ?? `HTTP ${response.status}`,
          variant: 'error',
        });
        return;
      }
      toast({
        title: t('syncErrors.dlq.toast.discardedTitle'),
        description: t('syncErrors.dlq.toast.discardedDesc', {
          id: truncate(discardTarget.job_id, 12),
        }),
        variant: 'success',
      });
      setDiscardTarget(null);
      setDiscardReason('');
      setDiscardError(null);
    } catch (err) {
      toast({
        title: t('syncErrors.dlq.toast.networkErrorTitle'),
        description: err instanceof Error ? err.message : 'Unknown',
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
      setRefreshTick((n) => n + 1);
    }
  }

  // ---- Bulk retry ----

  async function handleBulkRetry(): Promise<void> {
    if (selected.size === 0) return;
    setSubmitting(true);
    const ids = Array.from(selected);
    let success = 0;
    let failed = 0;
    try {
      const client = apiClient() as unknown as LooseClient;
      // Sequential to keep idempotency keys distinct + audit log clean.
      for (const id of ids) {
        const { error, response } = await client.POST(
          `/api/v1/admin/jobs/dlq/${encodeURIComponent(id)}/retry`,
          {
            headers: { 'Idempotency-Key': newIdempotencyKey() },
            body: {},
          },
        );
        if (error || !response.ok) failed += 1;
        else success += 1;
      }
      toast({
        title: t('syncErrors.dlq.toast.bulkRetryDoneTitle'),
        description:
          failed > 0
            ? t('syncErrors.dlq.toast.bulkRetryDoneDescFailed', {
                success,
                total: ids.length,
                failed,
              })
            : t('syncErrors.dlq.toast.bulkRetryDoneDesc', {
                success,
                total: ids.length,
              }),
        variant: failed === 0 ? 'success' : 'warning',
      });
      setSelected(new Set());
    } finally {
      setSubmitting(false);
      setBulkRetry(false);
      setRefreshTick((n) => n + 1);
    }
  }

  // ---- Bulk discard ----

  async function handleBulkDiscard(): Promise<void> {
    if (selected.size === 0) return;
    const reason = bulkDiscardReason.trim();
    if (reason.length < 1) {
      setBulkDiscardError(t('syncErrors.dlq.bulkDiscardDialog.errorTooShort'));
      return;
    }
    if (reason.length > 500) {
      setBulkDiscardError(t('syncErrors.dlq.bulkDiscardDialog.errorTooLong'));
      return;
    }
    setSubmitting(true);
    const ids = Array.from(selected);
    let success = 0;
    let failed = 0;
    try {
      const client = apiClient() as unknown as LooseClient;
      for (const id of ids) {
        const { error, response } = await client.POST(
          `/api/v1/admin/jobs/dlq/${encodeURIComponent(id)}/discard`,
          {
            headers: { 'Idempotency-Key': newIdempotencyKey() },
            body: { reason },
          },
        );
        if (error || !response.ok) failed += 1;
        else success += 1;
      }
      toast({
        title: t('syncErrors.dlq.toast.bulkDiscardDoneTitle'),
        description:
          failed > 0
            ? t('syncErrors.dlq.toast.bulkDiscardDoneDescFailed', {
                success,
                total: ids.length,
                failed,
              })
            : t('syncErrors.dlq.toast.bulkDiscardDoneDesc', {
                success,
                total: ids.length,
              }),
        variant: failed === 0 ? 'success' : 'warning',
      });
      setSelected(new Set());
      setBulkDiscardReason('');
      setBulkDiscardError(null);
    } finally {
      setSubmitting(false);
      setBulkDiscard(false);
      setRefreshTick((n) => n + 1);
    }
  }

  // ---- Pagination ----

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function goPage(p: number): void {
    const clamped = Math.max(1, Math.min(totalPages, p));
    updateParam('page', clamped === 1 ? '' : String(clamped));
  }

  const allChecked = rows.length > 0 && selected.size === rows.length;
  const someChecked = selected.size > 0 && selected.size < rows.length;

  // ---- Render ----

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title={t('syncErrors.dlq.title')}
        titleAddon={
          <span className="rounded-full border border-pw-border bg-pw-bg px-2.5 py-0.5 text-xs text-pw-text-muted">
            {total === 1
              ? t('syncErrors.dlq.totalJob', { count: total })
              : t('syncErrors.dlq.totalJobs', { count: total })}
          </span>
        }
        actionsAlign="center"
        actions={
          <Button
            variant="ghost"
            onClick={() => setRefreshTick((n) => n + 1)}
            disabled={loading}
          >
            {loading
              ? t('syncErrors.common.loading')
              : t('syncErrors.common.refresh')}
          </Button>
        }
      />

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-wrap gap-3 p-4">
          <div className="flex flex-col">
            <label className="mb-1 text-xs text-pw-text-muted">
              {t('syncErrors.dlq.filters.queue')}
            </label>
            <Select
              value={filterQueue === '' ? ALL_VALUE : filterQueue}
              onValueChange={(value) =>
                updateParam('queue_name', value === ALL_VALUE ? '' : value)
              }
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={t('syncErrors.dlq.queueOptions.all')}
                />
              </SelectTrigger>
              <SelectContent>
                {QUEUE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value || 'all'} value={opt.value || ALL_VALUE}>
                    {t(`syncErrors.dlq.queueOptions.${opt.labelKey}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col">
            <label className="mb-1 text-xs text-pw-text-muted">
              {t('syncErrors.dlq.filters.dateFrom')}
            </label>
            <Input
              type="date"
              value={filterDateFrom ? filterDateFrom.slice(0, 10) : ''}
              onChange={(e) => {
                const v = e.target.value;
                updateParam(
                  'date_from',
                  v ? new Date(`${v}T00:00:00.000Z`).toISOString() : '',
                );
              }}
            />
          </div>
          <div className="flex flex-col">
            <label className="mb-1 text-xs text-pw-text-muted">
              {t('syncErrors.dlq.filters.dateTo')}
            </label>
            <Input
              type="date"
              value={filterDateTo ? filterDateTo.slice(0, 10) : ''}
              onChange={(e) => {
                const v = e.target.value;
                updateParam(
                  'date_to',
                  v ? new Date(`${v}T23:59:59.999Z`).toISOString() : '',
                );
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Bulk action bar */}
      {selected.size > 0 ? (
        <div className="flex items-center justify-between rounded-md border border-pw-primary/30 bg-pw-primary/5 px-4 py-2">
          <span className="text-sm text-pw-text">
            {t('syncErrors.dlq.bulk.selected')}{' '}
            <strong>{selected.size}</strong>{' '}
            {t('syncErrors.dlq.bulk.jobSuffix')}
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => setBulkRetry(true)}
              disabled={submitting}
            >
              {t('syncErrors.dlq.bulk.retryAll')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => setBulkDiscard(true)}
              disabled={submitting}
            >
              {t('syncErrors.dlq.bulk.discardAll')}
            </Button>
            <Button variant="ghost" onClick={() => setSelected(new Set())}>
              {t('syncErrors.common.deselect')}
            </Button>
          </div>
        </div>
      ) : null}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-pw-border bg-pw-bg-muted text-left text-xs uppercase text-pw-text-muted">
                <tr>
                  <th className="w-10 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      ref={(el) => {
                        if (el) el.indeterminate = someChecked;
                      }}
                      onChange={toggleAll}
                      aria-label={t('syncErrors.common.selectAll')}
                    />
                  </th>
                  <th className="px-3 py-2">{t('syncErrors.dlq.table.queue')}</th>
                  <th className="px-3 py-2">{t('syncErrors.dlq.table.jobId')}</th>
                  <th className="px-3 py-2">{t('syncErrors.dlq.table.failedAt')}</th>
                  <th className="px-3 py-2">{t('syncErrors.dlq.table.attempts')}</th>
                  <th className="px-3 py-2">{t('syncErrors.dlq.table.error')}</th>
                  <th className="w-48 px-3 py-2 text-right">{t('syncErrors.dlq.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && !loading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-3 py-12 text-center text-pw-text-muted"
                    >
                      {filterQueue || filterDateFrom || filterDateTo
                        ? t('syncErrors.dlq.table.emptyFiltered')
                        : t('syncErrors.dlq.table.empty')}
                    </td>
                  </tr>
                ) : null}
                {rows.map((row) => (
                  <tr
                    key={row.job_id}
                    className="border-b border-pw-border last:border-b-0 hover:bg-pw-bg-muted/40"
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(row.job_id)}
                        onChange={() => toggleRow(row.job_id)}
                        aria-label={t('syncErrors.dlq.table.rowSelectAria', { id: row.job_id })}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={cn(
                          'inline-block rounded-full border px-2 py-0.5 text-xs',
                          queueBadgeClass(row.queue_name),
                        )}
                      >
                        {row.queue_name}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {truncate(row.job_id, 18)}
                    </td>
                    <td
                      className="px-3 py-2 text-pw-text-muted"
                      title={new Date(row.failed_at).toLocaleString()}
                    >
                      {relativeTime(row.failed_at, t)}
                    </td>
                    <td className="px-3 py-2">{row.attempts}</td>
                    <td className="px-3 py-2 text-pw-text-muted">
                      <span title={row.last_error_message}>
                        {truncate(row.last_error_message, 80)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setPayloadView(row)}
                        >
                          {t('syncErrors.dlq.actions.view')}
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setRetryTarget(row)}
                          disabled={submitting}
                        >
                          {t('syncErrors.dlq.actions.retry')}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            setDiscardTarget(row);
                            setDiscardReason('');
                            setDiscardError(null);
                          }}
                          disabled={submitting}
                        >
                          {t('syncErrors.dlq.actions.discard')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > 0 ? (
        <Pagination
          currentPage={page}
          pageCount={totalPages}
          totalRows={total}
          windowSize={5}
          onChange={goPage}
          testId="dlq-pagination"
        />
      ) : null}

      {/* Single retry confirm */}
      {retryTarget ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setRetryTarget(null)}
          title={t('syncErrors.dlq.retryDialog.title')}
          description={t('syncErrors.dlq.retryDialog.description', {
            id: truncate(retryTarget.job_id, 24),
            queue: retryTarget.queue_name,
          })}
          confirmLabel={t('syncErrors.dlq.retryDialog.confirm')}
          type="neutral"
          loading={submitting}
          onConfirm={() => void handleRetry(retryTarget)}
        />
      ) : null}

      {/* Single discard form */}
      {discardTarget ? (
        <Dialog open onOpenChange={(open) => !open && setDiscardTarget(null)}>
          <DialogHeader>
            <DialogTitle>{t('syncErrors.dlq.discardDialog.title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
              <p className="text-sm text-pw-text-muted">
                <span
                  dangerouslySetInnerHTML={{
                    __html: t('syncErrors.dlq.discardDialog.body', {
                      id: truncate(discardTarget.job_id, 24),
                      queue: discardTarget.queue_name,
                    }),
                  }}
                />
              </p>
              <div>
                <label className="mb-1 block text-xs text-pw-text-muted">
                  {t('syncErrors.dlq.discardDialog.reasonLabel')}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  className={cn(
                    'w-full rounded-md border border-pw-border p-2 text-sm',
                    discardError ? 'border-red-400' : '',
                  )}
                  rows={3}
                  maxLength={500}
                  value={discardReason}
                  onChange={(e) => {
                    setDiscardReason(e.target.value);
                    if (discardError) setDiscardError(null);
                  }}
                  placeholder={t('syncErrors.dlq.discardDialog.reasonPlaceholder')}
                />
                {discardError ? (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-300">{discardError}</p>
                ) : null}
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  onClick={() => setDiscardTarget(null)}
                  disabled={submitting}
                >
                  {t('syncErrors.common.cancel')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => void handleDiscard()}
                  disabled={submitting}
                >
                  {submitting
                    ? t('syncErrors.common.processing')
                    : t('syncErrors.dlq.discardDialog.confirm')}
                </Button>
              </div>
            </div>
        </Dialog>
      ) : null}

      {/* Bulk retry confirm */}
      {bulkRetry ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setBulkRetry(false)}
          title={t('syncErrors.dlq.bulkRetryDialog.title')}
          description={t('syncErrors.dlq.bulkRetryDialog.description', {
            count: selected.size,
          })}
          confirmLabel={t('syncErrors.dlq.bulkRetryDialog.confirm', {
            count: selected.size,
          })}
          type="neutral"
          loading={submitting}
          onConfirm={() => void handleBulkRetry()}
        />
      ) : null}

      {/* Bulk discard form */}
      {bulkDiscard ? (
        <Dialog open onOpenChange={(open) => !open && setBulkDiscard(false)}>
          <DialogHeader>
            <DialogTitle>
              {t('syncErrors.dlq.bulkDiscardDialog.title', { count: selected.size })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
              <p className="text-sm text-pw-text-muted">
                {t('syncErrors.dlq.bulkDiscardDialog.body', { count: selected.size })}
              </p>
              <div>
                <label className="mb-1 block text-xs text-pw-text-muted">
                  {t('syncErrors.dlq.bulkDiscardDialog.reasonLabel')}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  className={cn(
                    'w-full rounded-md border border-pw-border p-2 text-sm',
                    bulkDiscardError ? 'border-red-400' : '',
                  )}
                  rows={3}
                  maxLength={500}
                  value={bulkDiscardReason}
                  onChange={(e) => {
                    setBulkDiscardReason(e.target.value);
                    if (bulkDiscardError) setBulkDiscardError(null);
                  }}
                  placeholder={t('syncErrors.dlq.bulkDiscardDialog.reasonPlaceholder')}
                />
                {bulkDiscardError ? (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-300">{bulkDiscardError}</p>
                ) : null}
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  onClick={() => setBulkDiscard(false)}
                  disabled={submitting}
                >
                  {t('syncErrors.common.cancel')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => void handleBulkDiscard()}
                  disabled={submitting}
                >
                  {submitting
                    ? t('syncErrors.common.processing')
                    : t('syncErrors.dlq.bulkDiscardDialog.confirm', { count: selected.size })}
                </Button>
              </div>
            </div>
        </Dialog>
      ) : null}

      {/* Payload viewer */}
      {payloadView ? (
        <Dialog
          open
          onOpenChange={(open) => !open && setPayloadView(null)}
          className="max-w-3xl"
        >
          <DialogHeader>
            <DialogTitle>
              {t('syncErrors.dlq.payloadDialog.title', {
                id: truncate(payloadView.job_id, 32),
              })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-pw-text-muted">
                    {t('syncErrors.dlq.payloadDialog.queue')}
                  </span>{' '}
                  <span
                    className={cn(
                      'inline-block rounded-full border px-2 py-0.5 text-xs',
                      queueBadgeClass(payloadView.queue_name),
                    )}
                  >
                    {payloadView.queue_name}
                  </span>
                </div>
                <div>
                  <span className="text-pw-text-muted">
                    {t('syncErrors.dlq.payloadDialog.attempts')}
                  </span>{' '}
                  {payloadView.attempts}
                </div>
                <div>
                  <span className="text-pw-text-muted">
                    {t('syncErrors.dlq.payloadDialog.failedAt')}
                  </span>{' '}
                  {new Date(payloadView.failed_at).toLocaleString()}
                </div>
                <div className="col-span-2">
                  <span className="text-pw-text-muted">
                    {t('syncErrors.dlq.payloadDialog.error')}
                  </span>{' '}
                  <span className="text-red-600 dark:text-red-300">{payloadView.last_error_message}</span>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs text-pw-text-muted">
                  {t('syncErrors.dlq.payloadDialog.payloadJson')}
                </label>
                <pre className="max-h-96 overflow-auto rounded-md border border-pw-border bg-pw-bg-muted p-3 text-xs">
                  {JSON.stringify(payloadView.payload, null, 2)}
                </pre>
              </div>
              <div className="flex justify-end">
                <Button variant="ghost" onClick={() => setPayloadView(null)}>
                  {t('syncErrors.common.close')}
                </Button>
              </div>
            </div>
        </Dialog>
      ) : null}
    </div>
  );
}
